import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/hooks/useAuthStore';
import { toast } from 'sonner';
import {
  ArrowRight,
  ArrowLeft,
  TrendingUp,
  Users,
  Sparkles,
  CheckCircle2,
  Loader2,
} from 'lucide-react';

// Simple valuation multiplier model by stage
const STAGE_MULTIPLIERS: Record<
  string,
  { revMin: number; revMax: number; teamMin: number; teamMax: number }
> = {
  PRE_SEED: { revMin: 15, revMax: 25, teamMin: 200000, teamMax: 400000 },
  SEED: { revMin: 10, revMax: 20, teamMin: 300000, teamMax: 600000 },
  SERIES_A: { revMin: 8, revMax: 15, teamMin: 500000, teamMax: 800000 },
  SERIES_B: { revMin: 6, revMax: 12, teamMin: 700000, teamMax: 1000000 },
  GROWTH: { revMin: 5, revMax: 10, teamMin: 800000, teamMax: 1200000 },
};

function parseNumber(val: string): number {
  return Number(val.replace(/[,\s]/g, '')) || 0;
}

function formatCurrency(val: number): string {
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val.toLocaleString()}`;
}

function estimateValuation(revenue: number, growth: number, team: number, stage: string) {
  const mult = STAGE_MULTIPLIERS[stage] || STAGE_MULTIPLIERS.SEED;
  const growthBoost = Math.min(growth / 100, 2);

  // Revenue-based estimate
  const revLow = revenue * mult.revMin * (0.8 + growthBoost * 0.2);
  const revHigh = revenue * mult.revMax * (0.8 + growthBoost * 0.3);

  // Team-based floor
  const teamLow = team * mult.teamMin;
  const teamHigh = team * mult.teamMax;

  const low = Math.max(revLow, teamLow, 100_000);
  const high = Math.max(revHigh, teamHigh, low * 1.5);
  const mid = (low + high) / 2;

  return { low, mid, high };
}

interface OnboardingFlowProps {
  onComplete: () => void;
}

export function OnboardingFlow({ onComplete }: OnboardingFlowProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  const [data, setData] = useState({
    companyName: '',
    stage: 'SEED',
    jurisdiction: 'ZA',
    revenue: '',
    growth: '',
    team: '',
    founderName: '',
    founderShares: '1000',
  });

  const update = (field: string, value: string) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const valuation = useMemo(() => {
    return estimateValuation(
      parseNumber(data.revenue),
      parseNumber(data.growth),
      parseNumber(data.team),
      data.stage
    );
  }, [data.revenue, data.growth, data.team, data.stage]);

  const canProceedStep1 = data.companyName.trim().length > 0;
  const canProceedStep2 = parseNumber(data.revenue) > 0 || parseNumber(data.team) > 0;

  async function handleCreateCompany() {
    if (!user) {
      toast.error('You must be logged in');
      return;
    }

    setSaving(true);
    try {
      // Create company
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .insert({
          name: data.companyName,
          jurisdiction: data.jurisdiction,
          created_by: user.id,
        })
        .select()
        .single();

      if (companyError) throw companyError;

      // Assign owner role
      const { error: roleError } = await supabase.from('user_roles').insert({
        user_id: user.id,
        role: 'owner',
        company_id: company.id,
      });

      if (roleError && roleError.code !== '23505') throw roleError;

      // Create founder shareholder if provided
      if (data.founderName.trim()) {
        const { data: sh, error: shError } = await supabase
          .from('shareholders')
          .insert({
            company_id: company.id,
            name: data.founderName,
            email: user.email || null,
            entity_type: 'INDIVIDUAL',
          })
          .select()
          .single();

        if (shError) throw shError;

        const shares = parseNumber(data.founderShares);
        if (shares > 0 && sh) {
          await supabase.from('ledger_events').insert({
            company_id: company.id,
            shareholder_id: sh.id,
            event_type: 'ISSUANCE',
            shares,
            share_class: 'Ordinary',
            created_by: user.id,
            note_reference: 'Founding shares',
          });
        }
      }

      toast.success('Company created successfully!');
      setStep(5);
    } catch (error: any) {
      console.error('Error:', error);
      toast.error(error.message || 'Failed to create company');
    } finally {
      setSaving(false);
    }
  }

  const slideVariants = {
    enter: { opacity: 0, x: 40 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -40 },
  };

  return (
    <div className="w-full max-w-lg mx-auto">
      {/* Progress indicator */}
      <div className="flex items-center gap-1 mb-8">
        {[1, 2, 3, 4, 5].map((s) => (
          <div
            key={s}
            className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
              s <= step ? 'bg-primary' : 'bg-border'
            }`}
          />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* STEP 1: Quick Setup */}
        {step === 1 && (
          <motion.div
            key="step1"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25 }}
            className="space-y-6"
          >
            <div>
              <h2 className="text-2xl font-semibold text-foreground">Let's get you set up</h2>
              <p className="text-sm text-muted-foreground mt-1">
                This will take less than 2 minutes.
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company name</Label>
                <Input
                  id="companyName"
                  value={data.companyName}
                  onChange={(e) => update('companyName', e.target.value)}
                  placeholder="Acme Inc."
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label>Stage</Label>
                <Select value={data.stage} onValueChange={(v) => update('stage', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PRE_SEED">Pre-Seed</SelectItem>
                    <SelectItem value="SEED">Seed</SelectItem>
                    <SelectItem value="SERIES_A">Series A</SelectItem>
                    <SelectItem value="SERIES_B">Series B</SelectItem>
                    <SelectItem value="GROWTH">Growth</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Jurisdiction</Label>
                <Select value={data.jurisdiction} onValueChange={(v) => update('jurisdiction', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ZA">South Africa</SelectItem>
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="GB">United Kingdom</SelectItem>
                    <SelectItem value="AE">UAE</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button className="w-full" onClick={() => setStep(2)} disabled={!canProceedStep1}>
              Continue
              <ArrowRight className="h-4 w-4" />
            </Button>
          </motion.div>
        )}

        {/* STEP 2: Valuation Inputs */}
        {step === 2 && (
          <motion.div
            key="step2"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25 }}
            className="space-y-6"
          >
            <div>
              <h2 className="text-2xl font-semibold text-foreground">
                Let's estimate your valuation
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                We'll use simple inputs to give you a range.
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="revenue">Annual revenue (USD)</Label>
                <Input
                  id="revenue"
                  value={data.revenue}
                  onChange={(e) => update('revenue', e.target.value)}
                  placeholder="e.g. 500000"
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="growth">YoY growth (%)</Label>
                <Input
                  id="growth"
                  value={data.growth}
                  onChange={(e) => update('growth', e.target.value)}
                  placeholder="e.g. 80"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="team">Team size</Label>
                <Input
                  id="team"
                  value={data.team}
                  onChange={(e) => update('team', e.target.value)}
                  placeholder="e.g. 8"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button className="flex-1" onClick={() => setStep(3)} disabled={!canProceedStep2}>
                <Sparkles className="h-4 w-4" />
                Calculate Value
              </Button>
            </div>
          </motion.div>
        )}

        {/* STEP 3: Aha Moment — Valuation Result */}
        {step === 3 && (
          <motion.div
            key="step3"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25 }}
            className="space-y-6"
          >
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
                <TrendingUp className="h-3 w-3" />
                Estimated Valuation
              </div>
              <h2 className="text-2xl font-semibold text-foreground">Your estimated valuation</h2>
            </div>

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="rounded-xl border border-primary/20 bg-primary/5 p-8 text-center space-y-3"
            >
              <p className="text-4xl font-bold tabular-nums text-foreground">
                {formatCurrency(valuation.low)} – {formatCurrency(valuation.high)}
              </p>
              <p className="text-sm text-muted-foreground">
                Mid-point:{' '}
                <span className="font-semibold text-foreground">
                  {formatCurrency(valuation.mid)}
                </span>
              </p>
              <p className="text-xs text-muted-foreground">
                Based on your inputs, stage, and market benchmarks
              </p>
            </motion.div>

            <p className="text-xs text-muted-foreground text-center">
              This is a directional estimate. Use CapLedger's full valuation advisor for detailed
              analysis.
            </p>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="h-4 w-4" />
                Adjust
              </Button>
              <Button className="flex-1" onClick={() => setStep(4)}>
                <Users className="h-4 w-4" />
                Build Cap Table
              </Button>
            </div>
          </motion.div>
        )}

        {/* STEP 4: Cap Table Starter */}
        {step === 4 && (
          <motion.div
            key="step4"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25 }}
            className="space-y-6"
          >
            <div>
              <h2 className="text-2xl font-semibold text-foreground">Structure your ownership</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Start with your primary founder. You can add more shareholders later.
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="founderName">Founder name</Label>
                <Input
                  id="founderName"
                  value={data.founderName}
                  onChange={(e) => update('founderName', e.target.value)}
                  placeholder="e.g. Jane Smith"
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="founderShares">Founding shares</Label>
                <Input
                  id="founderShares"
                  value={data.founderShares}
                  onChange={(e) => update('founderShares', e.target.value)}
                  placeholder="e.g. 1000"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(3)}>
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                className="flex-1"
                onClick={handleCreateCompany}
                disabled={saving || !data.founderName.trim()}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                {saving ? 'Creating...' : 'Create Company'}
              </Button>
            </div>
          </motion.div>
        )}

        {/* STEP 5: Complete / Next Actions */}
        {step === 5 && (
          <motion.div
            key="step5"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25 }}
            className="space-y-6 text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
            >
              <CheckCircle2 className="h-16 w-16 text-primary mx-auto" />
            </motion.div>

            <div>
              <h2 className="text-2xl font-semibold text-foreground">You're ready</h2>
              <p className="text-sm text-muted-foreground mt-2">
                {data.companyName} is set up. Here's what to do next:
              </p>
            </div>

            <div className="space-y-2 text-left">
              {[
                'Add co-founders and investors',
                'Run a detailed valuation analysis',
                'Model your next funding round',
                'Generate your closing pack',
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg border border-border bg-card text-sm text-foreground"
                >
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  {item}
                </div>
              ))}
            </div>

            <Button className="w-full" onClick={onComplete}>
              Go to Dashboard
              <ArrowRight className="h-4 w-4" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
